import { useCallback, useEffect, useState } from 'react'
import { useAccessManagement } from '../hooks/useAccessManagement.js'
import { useAccessData } from '../hooks/useAccessData.js'

/**
 * InvitationView - Displays pending device invitations with accept/decline options
 * Shows invitation details and handles user actions
 */
function InvitationView({ invitations }) {
  const { acceptInvitation, declineInvitation } = useAccessManagement()
  const { getUserInfo } = useAccessData()
  const [inviterName, setInviterName] = useState('')

  // Get inviter name for first invitation
  useEffect(() => {
    if (invitations[0]?.invitedBy) {
      getUserInfo(invitations[0].invitedBy).then(user => {
        const name = user?.name || user?.displayName || user?.email || 'Unknown user'
        setInviterName(name)
      }).catch(() => {
        setInviterName('Unknown user')
      })
    }
  }, [invitations, getUserInfo])

  const handleAccept = useCallback(async () => {
    if (!invitations[0]) return
    try {
      await acceptInvitation(invitations[0].id)
    } catch (error) {
      console.error('Failed to accept invitation:', error)
    }
  }, [acceptInvitation, invitations])

  const handleDecline = useCallback(async () => {
    if (!invitations[0]) return
    try {
      await declineInvitation(invitations[0].id)
    } catch (error) {
      console.error('Failed to decline invitation:', error)
    }
  }, [declineInvitation, invitations])

  if (!invitations.length) {
    return null
  }

  const invitation = invitations[0]
  const { deviceId, permission } = invitation
  const inviter = inviterName || 'Unknown user'

  return (
    <div className="onboarding-hero-bg py-4">
      <div className="d-flex justify-content-center">
        <div className="card onboarding-card shadow-sm" style={{ maxWidth: '540px' }}>
          <div className="card-body text-center p-4">
            {/* Device icon container */}
            <div
              className="onboarding-icon-container mb-3"
              style={{
                width: '120px',
                height: '120px',
                margin: '0 auto',
                background: 'radial-gradient(circle at center, rgba(220,53,69,0.15), transparent)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src="/images/Altion-final.svg"
                alt="Revatek Device"
                style={{ width: '80%', height: '80%' }}
              />
            </div>
            
            {/* Invitation details */}
            <h1 className="display-6 fw-bold mb-2">You&rsquo;ve Been Invited to an Altion</h1>
            <p className="text-muted mb-3">
              Invited by <strong>{inviter}</strong> to access device <strong>{deviceId}</strong> as <strong>{permission}</strong>
            </p>
            
            {/* Accept/Decline buttons */}
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-success btn-lg px-4 shadow-lg flex-grow-1 flex-sm-grow-0"
                onClick={handleAccept}
              >
                <i className="bi bi-check-circle me-2" aria-hidden="true"></i>
                Accept
              </button>
              <button
                className="btn btn-outline-danger btn-lg px-4 shadow-lg flex-grow-1 flex-sm-grow-0"
                onClick={handleDecline}
              >
                <i className="bi bi-x-circle me-2" aria-hidden="true"></i>
                Decline
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvitationView
